import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography, TextField, Button, Chip, Stack, Alert, CircularProgress, Divider } from "@mui/material";
import { useAuth } from "../context/AuthContext";
import AuthPanel from "../components/AuthPanel";
import MfaSetup from "../components/MfaSetup";
import CalendarSync from "../components/CalendarSync";
import AvailabilityEditor, { Block } from "../components/AvailabilityEditor";
import PsychologistApi, { ProfileInput } from "../api/PsychologistApi";
import SchedulingApi from "../api/SchedulingApi";
import AppointmentApi, { Appointment } from "../api/AppointmentApi";
import AssignmentApi, { Assignment } from "../api/AssignmentApi";
import Clients from "../components/Clients";
import CatalogBrowser from "../components/CatalogBrowser";
import OrgApi, { OrgInfo, OrgDashboard, OrgMembershipInfo, OrgBillingUsage } from "../api/OrgApi";
import OrgBarometer from "../components/OrgBarometer";
import OrgQuarterReport from "../components/OrgQuarterReport";
import WerkgeverManual from "../components/WerkgeverManual";
import OnboardingKit from "../components/OnboardingKit";
import OutcomeCard from "../components/OutcomeCard";
import ProviderOnboarding from "../components/provider-profile/ProviderOnboarding";
import BasicsSection from "../components/provider-profile/BasicsSection";
import OfferSection from "../components/provider-profile/OfferSection";
import CredentialsSection from "../components/provider-profile/CredentialsSection";
import BillingSection from "../components/provider-profile/BillingSection";
import DocumentsSection from "../components/provider-profile/DocumentsSection";
import { emptyProfile, profileFromApi } from "../components/provider-profile/profileShared";
import { eur } from "../constants/funnel";

interface DashSection { key: string; label: string; intro?: string; node: React.ReactNode }

function DashboardLayout({ title, sections, banner }: { title: string; sections: DashSection[]; banner?: React.ReactNode }) {
  const { t } = useTranslation();
  const [active, setActive] = useState(sections[0]?.key);
  const current = sections.find((s) => s.key === active) || sections[0];
  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 5 } }}>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 3 }}>
        <Typography variant="h1" sx={{ fontSize: { xs: "1.8rem", sm: "2.4rem" }, m: 0 }}>{title}</Typography>
        <Box component={RouterLink} to="/mijn-gegevens" sx={{ fontSize: ".9rem", color: "var(--link)", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>{t("dsar.title")} →</Box>
      </Box>
      {banner}
      <Box sx={{ display: { md: "grid" }, gridTemplateColumns: { md: "230px 1fr" }, gap: { md: 4 }, alignItems: "start" }}>
        <Box sx={{ display: "flex", flexDirection: { xs: "row", md: "column" }, gap: 0.5, overflowX: { xs: "auto", md: "visible" }, pb: { xs: 1, md: 0 }, mb: { xs: 2, md: 0 }, position: { md: "sticky" }, top: { md: 24 } }}>
          {sections.map((s) => (
            <Box key={s.key} onClick={() => setActive(s.key)} role="button"
              sx={{ px: 2, py: 1.25, borderRadius: "var(--radius-md)", cursor: "pointer", whiteSpace: "nowrap", fontSize: ".95rem", fontWeight: current?.key === s.key ? 600 : 400, color: current?.key === s.key ? "primary.dark" : "text.primary", bgcolor: current?.key === s.key ? "var(--primary-50)" : "transparent", "&:hover": { bgcolor: current?.key === s.key ? "var(--primary-50)" : "var(--surface-alt)" } }}>
              {s.label}
            </Box>
          ))}
        </Box>
        <Box sx={{ p: { xs: 3, sm: 4 }, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)", boxShadow: "var(--shadow-sm)" }}>
          <Typography variant="h2" sx={{ fontSize: "1.3rem", mb: current?.intro ? 0.5 : 2.5 }}>{current?.label}</Typography>
          {current?.intro && <Typography sx={{ color: "text.secondary", fontSize: ".92rem", mb: 2.5 }}>{current.intro}</Typography>}
          {current?.node}
        </Box>
      </Box>
    </Container>
  );
}

function AppointmentList({ appts }: { appts: Appointment[] }) {
  const { t, i18n } = useTranslation();
  if (appts.length === 0) return <Typography sx={{ color: "text.secondary" }}>{t("dashboard.noAppointments")}</Typography>;
  const fmt = new Intl.DateTimeFormat(i18n.language, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  return (
    <Stack divider={<Divider />} spacing={0}>
      {appts.map((a) => (
        <Box key={a._id} component={RouterLink} to={`/afspraak/${a._id}`} sx={{ display: "flex", alignItems: "center", gap: 2, py: 1.5, textDecoration: "none", color: "text.primary" }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 600 }}>{t(`booking.type.${a.type}`)}</Typography>
            <Typography sx={{ fontSize: ".9rem", color: "text.secondary", textTransform: "capitalize" }}>{fmt.format(new Date(a.start))}</Typography>
          </Box>
          <Chip size="small"
            label={t(`appointment.status${a.status === "confirmed" ? "Confirmed" : a.status === "cancelled" ? "Cancelled" : "Pending"}`)}
            color={a.status === "confirmed" ? "success" : a.status === "cancelled" ? "default" : "warning"} />
        </Box>
      ))}
    </Stack>
  );
}

function PsychView() {
  const { t } = useTranslation();
  const [profile, setProfile] = useState<ProfileInput>(emptyProfile());
  // null = nog aan het laden; false = wizard tonen i.p.v. de dashboard-tabs.
  const [onboarded, setOnboarded] = useState<boolean | null>(null);
  const [verified, setVerified] = useState("");
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [appts, setAppts] = useState<Appointment[]>([]);
  const [msg, setMsg] = useState("");
  const [saveErr, setSaveErr] = useState("");
  const [payouts, setPayouts] = useState<{ connected: boolean; payoutsEnabled: boolean; detailsSubmitted?: boolean; stripeConfigured: boolean } | null>(null);
  const [payoutMsg, setPayoutMsg] = useState("");

  const load = () => {
    PsychologistApi.payoutsStatus().then(setPayouts).catch(() => {});
    PsychologistApi.getMine().then((p) => {
      setOnboarded(!!p?.onboardingCompletedAt);
      if (p) {
        setProfile(profileFromApi(p));
        setVerified(p.verifiedState || "");
      }
    }).catch(() => setOnboarded(false));
    SchedulingApi.getMyAvailability().then((a) => setBlocks(a.weeklyBlocks || [])).catch(() => {});
    AppointmentApi.list().then(setAppts).catch(() => {});
  };
  useEffect(load, []);

  const saveProfile = async () => {
    setSaveErr("");
    try {
      await PsychologistApi.updateMine({ ...profile, rateCents: profile.coachingRateCents ?? profile.rateCents });
      const p = await PsychologistApi.getMine();
      setVerified(p?.verifiedState || "");
      setMsg(t("dashboard.saved"));
    } catch (e: any) {
      setSaveErr(e?.response?.data?.error || t("vetting.saveError"));
    }
  };
  const saveAvailability = async () => { await SchedulingApi.setMyAvailability({ weeklyBlocks: blocks, exceptions: [] }); setMsg(t("dashboard.saved")); };
  const onboardPayouts = async () => {
    setPayoutMsg("");
    const r = await PsychologistApi.payoutsOnboard();
    if (r.url) window.location.href = r.url;
    else setPayoutMsg(t("payouts.stub"));
  };

  const payoutsNode = (
    <Stack spacing={2}>
      <Typography sx={{ color: "text.secondary", m: 0 }}>{t("payouts.feeNote")}</Typography>
      {payouts?.payoutsEnabled ? (
        <Alert severity="success">{t("payouts.active")}</Alert>
      ) : (
        <>
          <Typography sx={{ m: 0 }}>{payouts?.connected ? t("payouts.incomplete") : t("payouts.intro")}</Typography>
          <Button variant="contained" onClick={onboardPayouts} sx={{ alignSelf: "flex-start" }}>{payouts?.connected ? t("payouts.resume") : t("payouts.connect")}</Button>
          {payoutMsg && <Alert severity="info">{payoutMsg}</Alert>}
        </>
      )}
    </Stack>
  );

  const saveBlock = (
    <>
      {saveErr && <Alert severity="error" onClose={() => setSaveErr("")}>{saveErr}</Alert>}
      <Button variant="contained" onClick={saveProfile} disabled={!profile.displayName} sx={{ alignSelf: "flex-start" }}>{t("dashboard.save")}</Button>
    </>
  );

  const profileNode = (
    <Stack spacing={2.5}>
      <BasicsSection profile={profile} onChange={setProfile} />
      <Divider />
      <OfferSection profile={profile} onChange={setProfile} />
      {saveBlock}
    </Stack>
  );

  const credentialsNode = (
    <Stack spacing={2.5}>
      <Typography sx={{ fontSize: ".88rem", color: "text.secondary", m: 0 }}>{t("vetting.credentialsHint")}</Typography>
      <CredentialsSection profile={profile} onChange={setProfile} />
      {saveBlock}
      <Divider />
      <Box>
        <Typography variant="h3" sx={{ fontSize: "1.05rem", mb: 0.5 }}>{t("vetting.documents")}</Typography>
        <Typography sx={{ fontSize: ".88rem", color: "text.secondary", mb: 2 }}>{t("vetting.documentsHint")}</Typography>
        <DocumentsSection />
      </Box>
    </Stack>
  );

  const billingNode = (
    <Stack spacing={2.5}>
      <Typography sx={{ fontSize: ".88rem", color: "text.secondary", m: 0 }}>{t("vetting.billingHint")}</Typography>
      <BillingSection profile={profile} onChange={setProfile} />
      {saveBlock}
    </Stack>
  );

  const sections: DashSection[] = [
    { key: "profile", label: t("dashboard.profile"), node: profileNode },
    { key: "credentials", label: t("vetting.credentialsTitle"), node: credentialsNode },
    { key: "billing", label: t("vetting.billingTitle"), node: billingNode },
    { key: "availability", label: t("dashboard.availability"), node: (<><AvailabilityEditor blocks={blocks} onChange={setBlocks} /><Button variant="contained" onClick={saveAvailability} sx={{ mt: 2 }}>{t("dashboard.save")}</Button></>) },
    { key: "calendar", label: t("calendar.title"), node: <CalendarSync /> },
    { key: "appointments", label: t("dashboard.appointments"), node: <AppointmentList appts={appts} /> },
    { key: "clients", label: t("assign.clientsTab"), node: <Clients /> },
    { key: "library", label: t("assign.libraryTab"), node: (<><Typography sx={{ color: "text.secondary", mb: 2 }}>{t("assign.libraryIntro")}</Typography><CatalogBrowser /></>) },
    { key: "payouts", label: t("payouts.title"), node: payoutsNode },
  ];

  if (onboarded === null) return <Box sx={{ minHeight: "40dvh", display: "grid", placeItems: "center" }}><CircularProgress sx={{ color: "primary.main" }} /></Box>;
  if (!onboarded) return <ProviderOnboarding onDone={() => { setOnboarded(true); load(); }} />;

  const stateSeverity = verified === "verified" ? "success" : verified === "rejected" ? "error" : "info";
  const banner = (
    <>
      {verified && verified !== "verified" && <Alert severity={stateSeverity} sx={{ mb: 2 }}>{t(`vetting.stateBanner.${verified}`)}</Alert>}
      {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg("")}>{msg}</Alert>}
    </>
  );

  return <DashboardLayout title={t("dashboard.psychologistTitle")} sections={sections} banner={banner} />;
}

function OrgAdminView() {
  const { t } = useTranslation();
  const [org, setOrg] = useState<OrgInfo | null>(null);
  const [dash, setDash] = useState<OrgDashboard | null>(null);
  const [billingMsg, setBillingMsg] = useState("");
  const [usage, setUsage] = useState<OrgBillingUsage | null>(null);
  const [capInput, setCapInput] = useState<number | "">("");
  const [poolAdd, setPoolAdd] = useState<number | "">("");
  // Kostendrempel-alert in hele euro's ("" = geen drempel ingesteld).
  const [alertInput, setAlertInput] = useState<number | "">("");
  useEffect(() => {
    OrgApi.myOrg().then((o) => { setOrg(o); setCapInput(o.fairUseCapPerMember); }).catch(() => {});
    OrgApi.dashboard().then(setDash).catch(() => {});
  }, []);
  useEffect(() => {
    OrgApi.billingUsage().then((u) => { setUsage(u); setAlertInput(u.costAlertMonthlyCents ? Math.round(u.costAlertMonthlyCents / 100) : ""); }).catch(() => {});
  }, []);
  if (!org) return <Box sx={{ minHeight: "40dvh", display: "grid", placeItems: "center" }}><CircularProgress sx={{ color: "primary.main" }} /></Box>;

  const orgNode = (
    <Stack spacing={2.5}>
      <Typography sx={{ m: 0 }}>{t("org.members")}: <strong>{org.memberCount}</strong>{org.seatLimit ? ` / ${org.seatLimit}` : ""}</Typography>
      <Typography sx={{ m: 0 }}>{t("org.capLine", { count: org.fairUseCapPerMember })}</Typography>
      <Box sx={{ p: 3, borderRadius: "var(--radius-lg)", bgcolor: "var(--primary-50)", border: "1px solid var(--primary-100)" }}>
        <Typography variant="caption" sx={{ color: "primary.dark", textTransform: "uppercase", letterSpacing: ".05em" }}>{t("org.inviteCode")}</Typography>
        <Typography sx={{ fontFamily: "monospace", fontSize: "1.8rem", letterSpacing: ".12em", my: 0.5 }}>{org.inviteCode}</Typography>
        <Typography sx={{ color: "text.secondary", fontSize: ".9rem", m: 0 }}>{t("org.inviteHint")}</Typography>
      </Box>
    </Stack>
  );
  const insightsNode = !dash ? <CircularProgress size={24} sx={{ color: "primary.main" }} /> : dash.suppressed ? (
    <Alert severity="info">{t("org.suppressed", { k: dash.kThreshold })}</Alert>
  ) : (
    <Stack spacing={2}>
      {/* Zelfde kerncijfer-kaarten als het kwartaalrapport — één designtaal. */}
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
        {[[String(dash.activeMembers), t("org.activeMembers")], [String(dash.totalSessions), t("org.totalSessions")]].map(([v, l]) => (
          <Box key={l} sx={{ flex: "1 1 150px", maxWidth: 260, p: 2, borderRadius: "var(--radius-md)", bgcolor: "var(--primary-50)", border: "1px solid var(--primary-100)" }}>
            <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, m: 0, color: "primary.dark" }}>{v}</Typography>
            <Typography sx={{ fontSize: ".85rem", m: 0 }}>{l}</Typography>
          </Box>
        ))}
      </Box>
      {/* Uitkomstmeting (V-18): live k>=10-kwartaalaggregaat — zelfde
          suppressie als het kwartaalrapport. */}
      {dash.outcome && !dash.outcome.suppressed && (
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          {[[`${dash.outcome.progressMean}/5`, t("org.outcomeProgress")], [`${dash.outcome.usefulnessMean}/5`, t("org.outcomeUsefulness", { n: dash.outcome.n })]].map(([v, l]) => (
            <Box key={l} sx={{ flex: "1 1 150px", maxWidth: 260, p: 2, borderRadius: "var(--radius-md)", bgcolor: "var(--primary-50)", border: "1px solid var(--primary-100)" }}>
              <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, m: 0, color: "primary.dark" }}>{v}</Typography>
              <Typography sx={{ fontSize: ".85rem", m: 0 }}>{l}</Typography>
            </Box>
          ))}
        </Box>
      )}
      {dash.outcome?.suppressed && (
        <Typography sx={{ fontSize: ".85rem", color: "text.secondary", m: 0 }}>{t("org.outcomeSuppressed")}</Typography>
      )}
      {dash.sessionsByType && Object.keys(dash.sessionsByType).length > 0 && (
        <Box>
          <Typography sx={{ fontSize: ".9rem", color: "text.secondary", mb: 1 }}>{t("org.sessionsByType")}</Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
            {Object.entries(dash.sessionsByType).map(([type, n]) => <Chip key={type} label={`${t(`booking.type.${type}`)}: ${n}`} variant="outlined" />)}
          </Stack>
        </Box>
      )}
    </Stack>
  );

  const saveCap = async () => {
    setBillingMsg("");
    try {
      const r = await OrgApi.updateBillingSettings({ fairUseCapPerMember: Number(capInput) });
      setOrg({ ...org, fairUseCapPerMember: r.fairUseCapPerMember });
      setBillingMsg(t("billing.capSaved"));
    } catch { setBillingMsg(t("billing.saveError")); }
  };
  const grantPool = async () => {
    setBillingMsg("");
    try {
      const r = await OrgApi.grantPool(Number(poolAdd));
      setOrg({ ...org, overagePoolRemaining: r.overagePoolRemaining, overagePoolGrantedTotal: r.overagePoolGrantedTotal });
      setPoolAdd("");
      setBillingMsg(t("billing.poolSaved"));
    } catch { setBillingMsg(t("billing.saveError")); }
  };
  // Kostendrempel-alert (aggregaten only): leeg veld = drempel uitschakelen.
  const saveAlert = async () => {
    setBillingMsg("");
    try {
      const r = await OrgApi.updateBillingSettings({ costAlertMonthlyCents: alertInput === "" ? null : Number(alertInput) * 100 });
      if (usage) setUsage({ ...usage, costAlertMonthlyCents: r.costAlertMonthlyCents });
      setBillingMsg(t("billing.costAlertSaved"));
    } catch { setBillingMsg(t("billing.saveError")); }
  };
  // Usage-based billing: k-safe by construction — month TOTALS per session
  // type, the collective cap and pool totals. Never anything per employee.
  const usageBillingNode = (
    <Stack spacing={2.5}>
      <Typography sx={{ m: 0 }}>{t("billing.usageIntro")}</Typography>
      {usage && (
        <Box sx={{ p: 2.5, borderRadius: "var(--radius-md)", bgcolor: "var(--surface-alt)", border: "1px solid var(--border)" }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>{t("billing.usageMonth", { month: usage.month, year: usage.year })}</Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1, mb: 1.5 }}>
            {Object.entries(usage.countsByType).map(([type, n]) => <Chip key={type} label={`${t(`booking.type.${type}`)}: ${n}`} variant="outlined" />)}
            {usage.totalSessions === 0 && <Typography sx={{ color: "text.secondary", m: 0 }}>{t("billing.usageNone")}</Typography>}
          </Stack>
          <Typography sx={{ m: 0, fontSize: ".95rem" }}>{t("billing.estimatedInvoice")}: <strong>{eur(usage.estimatedInvoiceCents)}</strong></Typography>
          <Typography sx={{ m: 0, fontSize: ".9rem", color: "text.secondary" }}>{t("billing.annualFee", { tier: usage.annualFee.tierLabel, fee: eur(usage.annualFee.feeCentsExclVat) })}</Typography>
        </Box>
      )}
      <Box>
        <Typography variant="h3" sx={{ fontSize: "1.05rem", mb: 0.5 }}>{t("billing.capTitle")}</Typography>
        <Typography sx={{ fontSize: ".88rem", color: "text.secondary", mb: 1.5 }}>{t("billing.capHint")}</Typography>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <TextField size="small" type="number" label={t("billing.capLabel")} value={capInput} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCapInput(e.target.value === "" ? "" : Number(e.target.value))} sx={{ maxWidth: 220 }} />
          <Button variant="outlined" onClick={saveCap} disabled={capInput === "" || Number(capInput) === org.fairUseCapPerMember}>{t("dashboard.save")}</Button>
        </Stack>
      </Box>
      <Box>
        <Typography variant="h3" sx={{ fontSize: "1.05rem", mb: 0.5 }}>{t("billing.costAlertTitle")}</Typography>
        <Typography sx={{ fontSize: ".88rem", color: "text.secondary", mb: 1.5 }}>{t("billing.costAlertHint")}</Typography>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <TextField size="small" type="number" label={t("billing.costAlertLabel")} value={alertInput} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAlertInput(e.target.value === "" ? "" : Number(e.target.value))} sx={{ maxWidth: 220 }} />
          <Button variant="outlined" onClick={saveAlert}>{t("dashboard.save")}</Button>
        </Stack>
      </Box>
      <Box>
        <Typography variant="h3" sx={{ fontSize: "1.05rem", mb: 0.5 }}>{t("billing.poolTitle")}</Typography>
        <Typography sx={{ fontSize: ".88rem", color: "text.secondary", mb: 1.5 }}>{t("billing.poolHint")}</Typography>
        <Typography sx={{ m: 0, mb: 1.5 }}>{t("billing.poolStatus", { remaining: org.overagePoolRemaining, granted: org.overagePoolGrantedTotal })}</Typography>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <TextField size="small" type="number" label={t("billing.poolAddLabel")} value={poolAdd} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPoolAdd(e.target.value === "" ? "" : Number(e.target.value))} sx={{ maxWidth: 220 }} />
          <Button variant="outlined" onClick={grantPool} disabled={poolAdd === "" || Number(poolAdd) < 1}>{t("billing.poolAdd")}</Button>
        </Stack>
      </Box>
      {/* Jaaroverzicht-CSV (manual §3.3) — factuurgranulariteit: aantallen × type × maand. */}
      <Button variant="outlined" onClick={() => OrgApi.downloadUsageYearCsv(new Date().getFullYear()).catch(() => setBillingMsg(t("billing.saveError")))} sx={{ alignSelf: "flex-start" }}>
        {t("billing.exportYearCsv", { year: new Date().getFullYear() })}
      </Button>
      <Typography sx={{ m: 0, fontSize: ".85rem", color: "text.secondary" }}>{t("billing.usageExplain")}</Typography>
      {billingMsg && <Alert severity="info" onClose={() => setBillingMsg("")}>{billingMsg}</Alert>}
    </Stack>
  );
  const sections: DashSection[] = [
    { key: "org", label: t("org.section"), intro: t("org.tabIntro.org"), node: orgNode },
    { key: "insights", label: t("org.insights"), intro: t("org.tabIntro.insights"), node: insightsNode },
    { key: "barometer", label: t("barometer.section"), intro: t("org.tabIntro.barometer"), node: <OrgBarometer /> },
    { key: "report", label: t("orgReport.tab"), intro: t("org.tabIntro.report"), node: <OrgQuarterReport /> },
    { key: "billing", label: t("billing.title"), intro: t("org.tabIntro.billing"), node: usageBillingNode },
    { key: "kit", label: t("orgKit.tab"), intro: t("org.tabIntro.kit"), node: <OnboardingKit org={org} /> },
    { key: "manual", label: t("orgManual.tab"), intro: t("org.tabIntro.manual"), node: <WerkgeverManual /> },
  ];
  return <DashboardLayout title={t("org.adminTitle")} sections={sections} />;
}

function ClientView() {
  const { t } = useTranslation();
  const [appts, setAppts] = useState<Appointment[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [membership, setMembership] = useState<OrgMembershipInfo | null>(null);
  const [code, setCode] = useState("");
  const [joinErr, setJoinErr] = useState("");
  useEffect(() => {
    AppointmentApi.list().then(setAppts).catch(() => {});
    AssignmentApi.list().then(setAssignments).catch(() => {});
    OrgApi.membership().then(setMembership).catch(() => {});
  }, []);
  const join = async () => {
    setJoinErr("");
    try { await OrgApi.join(code.trim()); setMembership(await OrgApi.membership()); setCode(""); }
    catch { setJoinErr(t("org.joinError")); }
  };

  const orgNode = membership ? (
    <Stack spacing={1.5}>
      <Alert severity="success">{t("org.covered", { org: membership.orgName })}</Alert>
      <Typography sx={{ m: 0 }}>{t("org.coverage", { remaining: membership.remaining, allowance: membership.allowance })}</Typography>
    </Stack>
  ) : (
    <Stack spacing={2} sx={{ maxWidth: 360 }}>
      <Typography sx={{ m: 0 }}>{t("org.joinPrompt")}</Typography>
      <TextField label={t("org.codeLabel")} value={code} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCode(e.target.value)} />
      {joinErr && <Alert severity="error">{joinErr}</Alert>}
      <Button variant="contained" onClick={join} disabled={!code.trim()} sx={{ alignSelf: "flex-start" }}>{t("org.join")}</Button>
    </Stack>
  );

  const sections: DashSection[] = [
    { key: "appointments", label: t("dashboard.appointments"), node: <AppointmentList appts={appts} /> },
  ];
  if (assignments.length > 0) {
    sections.push({
      key: "recommended", label: t("assign.clientTitle"), node: (
        <Stack divider={<Divider />} spacing={0}>
          {assignments.map((a) => (
            <Box key={a.id} component={RouterLink}
              to={a.kind === "selftest" ? `/zelfhulp/test/${a.selfTestSlug}` : `/zelfhulp/${a.courseSlug}`}
              sx={{ display: "block", py: 1.5, textDecoration: "none", color: "text.primary" }}>
              <Typography sx={{ fontWeight: 600 }}>
                {a.kind === "selftest" ? a.selfTestTitle : a.courseTitle}{a.moduleTitle ? ` · ${a.moduleTitle}` : ""}
              </Typography>
              {a.note && <Typography sx={{ fontSize: ".9rem", color: "text.secondary" }}>“{a.note}”</Typography>}
            </Box>
          ))}
        </Stack>
      ),
    });
  }
  sections.push({ key: "org", label: t("org.section"), node: orgNode });

  return <DashboardLayout title={t("dashboard.clientTitle")} sections={sections} banner={<OutcomeCard />} />;
}

export default function Dashboard() {
  const { user, loading } = useAuth();
  const { t } = useTranslation();
  // Anyone listed as an admin of an organisation sees the org view — regardless
  // of the exact role string (a platform_admin can also be an org admin).
  const [orgAdmin, setOrgAdmin] = useState<boolean | null>(null);
  useEffect(() => {
    if (user && user.role !== "psychologist") OrgApi.myOrg().then(() => setOrgAdmin(true)).catch(() => setOrgAdmin(false));
  }, [user]);

  const spinner = <Box sx={{ minHeight: "40dvh", display: "grid", placeItems: "center" }}><CircularProgress sx={{ color: "primary.main" }} /></Box>;
  if (loading) return spinner;
  if (!user) {
    return (
      <Container maxWidth="xs" sx={{ py: { xs: 6, sm: 10 } }}>
        <Typography sx={{ mb: 2 }}>{t("dashboard.loginPrompt")}</Typography>
        <AuthPanel initialMode="login" onSuccess={() => {}} />
      </Container>
    );
  }
  // Admins & psychologists must have MFA — gate the dashboard until it's set up.
  if (user.mfaRequired) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 5, sm: 8 } }}>
        <Typography variant="h1" sx={{ fontSize: { xs: "1.8rem", sm: "2.2rem" }, mb: 1 }}>{t("mfa.gateTitle")}</Typography>
        <Typography sx={{ color: "text.secondary", mb: 3 }}>{t("mfa.gateBody")}</Typography>
        <MfaSetup />
      </Container>
    );
  }
  if (user.role === "psychologist") return <PsychView />;
  if (orgAdmin === null) return spinner; // still checking org-admin status
  if (orgAdmin) return <OrgAdminView />;
  return <ClientView />;
}
